/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, memo, useCallback, useEffect, useRef, useState } from "react"
import { Alert, Appearance, ImageStyle, Linking, SafeAreaView, TextStyle, TouchableOpacity, ViewStyle } from "react-native"
import { AutoImage, Button, Screen, Text, FormControl, PasswordStrengthMeterBar, JalaIcon } from "../../../components"
import { FormikProps, FormikValues, FastField, Formik} from "formik"
import { TxKeyPath, translate } from "../../../i18n"
import { goBack } from "../../../navigators"
import { color } from "../../../theme"
import { createAxios } from "../../../utils/axios"
import { PopUp } from "../../profile/setting-account/change-password/change-password-screen"
import { PasswordResetForm } from "../../../components/form/password-reset-form"
import { Modal, View, useDisclose } from "native-base"
import _ from "lodash"
import OTPInputView from '@twotalltotems/react-native-otp-input'
import { presetsLight } from "../../../components/text/text.presets"
import moment from "moment"
import * as yup from "yup"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import JalaAnalytic from "../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flexGrow: 1
}

const IMAGE: ImageStyle = {
  alignSelf: 'center',
  resizeMode: 'contain',
  height: 170,
  marginBottom: 24
}

const LINK_BUTTON: ViewStyle = {
  alignItems: 'center',
  marginTop: 16
}

const OTP_CONTAINER: ViewStyle = {
  height: 44,
}

const OTP_INPUT: TextStyle = {
  ...presetsLight.title1,
  textAlign: "center"
}

const VERIF_BUTTON = (isWA, buttonColor, isError): ViewStyle => ({
  backgroundColor: isError ? color.palette.gray500 : buttonColor,
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: isWA ? 16 : 0
})

const BUTTON_CONTACT: ViewStyle = {
  width: '40%',
  borderColor: color.palette.redDark
}

const PADDING: ViewStyle = {
  paddingHorizontal: 5
}

const ResponseModal: FC<{
  colorScheme?: "light" | "dark",
  isSuccess?: boolean,
  onClose?: () => void,
  isOpen?: boolean,
  onContactPress?: () => void
}> = observer(function ResponseModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), isSuccess, onClose, isOpen, onContactPress } = props;
  const title = isSuccess ? translate("passwordResetScreen.modalTitleSuccess") : translate("passwordResetScreen.modalTitleFailed")
  const subtitle = isSuccess ? translate("passwordResetScreen.modalSubtitleSuccess") : translate("passwordResetScreen.modalSubtitleFailed")

  const onContact = useCallback(() => {
    onContactPress();
    onClose();
  }, [onClose, onContactPress])

  return (
    <Modal isOpen={isOpen} onClose={onClose} padding="16px" marginTop="10px" alignSelf="center">
      <SafeAreaView />
      <View
        flexDirection="row"
        marginTop="16px"
        rounded="5px"
        padding="10px"
        alignItems="flex-start"
        position="absolute"
        top={20}
        backgroundColor={isSuccess ? "#E8F0FF" : color.palette.red300}
      >
        <JalaIcon name="info-1" size={18} color={isSuccess ? color.palette.blueDark : color.palette.redDark} />
        <View ml="5px" width="88%">
          <Text
            text={title}
            preset="title4"
            colorScheme={colorScheme}
            color={isSuccess ? color.palette.blueDark : color.palette.redDark}
          />
          <Text
            text={subtitle}
            preset="body3"
            colorScheme={colorScheme}
            textAlign="left"
            my="5px"
            color={isSuccess ? color.palette.gray800 : color.palette.red700}
          />
          {!isSuccess && <Button
            testID="jala:id/delete-yes-button"
            tx="verificationScreen.contactUs"
            preset="outline"
            textPreset="label"
            textColor={color.palette.redDark}
            style={BUTTON_CONTACT}
            onPress={onContact}
          />}
        </View>
        <TouchableOpacity style={PADDING} onPress={onClose}>
          <JalaIcon name="close" size={18} color={color.palette.gray800} />
        </TouchableOpacity>
      </View>
    </Modal>
  )
})

const OTPInput: FC<{
  colorScheme?: "light"|"dark",
  errors?: any,
  handleChangeOTP?: (value: string) => void
}> = memo(function OTPInput(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    errors, handleChangeOTP
  } = props
  return (
    <FormControl
      testID="jala:id/otp-input"
      isRequired
      labelPreset="title3"
      colorScheme={colorScheme}
      errorMessage={!!errors && translate("verificationScreen.codeVerificationError")}
      // style={FORM_CONTROL}
    >
      <OTPInputView
        style={OTP_CONTAINER}
        pinCount={6}
        onCodeChanged={handleChangeOTP}
        codeInputFieldStyle={OTP_INPUT}
        keyboardAppearance={colorScheme}
        autoFocusOnLoad={false}
      />
    </FormControl>
  )
})

const PasswordField: FC<{colorScheme?: "light" | "dark", name?: string, isValid?: boolean, handleSubmit?: any, isConfirmation?: boolean}> = observer(function PasswordField(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), name, isValid, handleSubmit, isConfirmation = false } = props

  return (
    <FastField name={name}>
      {({ field, form: { handleChange, handleBlur }, meta }) => (
        <FormControl
          isRequired
          isInvalid={meta.touched && !!meta.error}
          // labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
          // labelPreset="title3"
          colorScheme={colorScheme}
          inputProps={{
            testID: `jala:id/${field.name}-input`,
            // ref: input[field.name],
            type: "password",
            autoCapitalize: "none",
            autoComplete: "password",
            autoCorrect: false,
            textContentType: "newPassword",
            // returnKeyType: nextInput[field.name] ? "next" : "go",
            onSubmitEditing: () => isValid && handleSubmit(),
            placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
            defaultValue: meta.initialValue,
            onChangeText: handleChange(field.name),
            onBlur: handleBlur(field.name)
          }}
          helperTextComponent={!isConfirmation && <PasswordStrengthMeterBar colorScheme={colorScheme} value={field.value} />}
          errorMessage={meta.error}
          // style={FORM_CONTROL}
        />
      )}
    </FastField>
  )
})

const InputEmailOrPhone: FC<{
  colorScheme?: "light" | "dark",
  onSetMode?: (mode?: string) => void,
  onSetValue?: (phone?: string, via?: string) => void,
}> = observer(function InputEmailOrPhone(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), onSetMode, onSetValue } = props

  const [ isError, setIsError ] = useState<boolean>(false);
  const [ message, setMessage ] = useState<any>(null);
  const [ isVisible, setVisible ] = useState<boolean>(false);
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ isPhoneMode, setIsPhoneMode ] = useState<boolean>(false);

  const image = require('./shrimp_confuse.png')

  const resetForm = useRef<FormikProps<FormikValues>>()
  const resetFormRef = useCallback((node) => {
    resetForm.current = node;
    if (resetForm.current) {
      return isPhoneMode
        ? setIsError(!(resetForm.current.values.phone !== null && resetForm.current.isValid))
        : setIsError(!(resetForm.current.values.email !== null && resetForm.current.isValid))
    }
  }, [isPhoneMode])

  const onRequestOTP = useCallback((via) => {
    if (via === 'wa') {
      JalaAnalytic.log("Mobile app : forget password - send otp via whatsapp")
    } else {
      JalaAnalytic.log("Mobile app : forget password - send otp via sms")
    }
    const phone = resetForm.current?.values?.phone || '';
    onSetValue(phone, via);
    onSetMode('otp')
  }, [onSetMode, onSetValue])

  useEffect(() => {
    if (isPhoneMode) JalaAnalytic.log("Mobile app : forget password via phone number  - visit")
  }, [isPhoneMode])

  const onSubmit = useCallback(({ email, phone }) => async(via) => {
    setLoading(true);
    await createAxios({ timeout: 60000 }).post('password/email', { email })
      .then(() => {
        setMessage(null);
      })
      .catch((e) => {
        if (e.response && e.response.data) {
          const { errors, message } = e.response.data;
          const errorMessage = errors && errors.email ? translate("passwordResetScreen.errorMessage") : message || 'error';
          setMessage(errorMessage);
        }
      })
      .finally(() => {
        setLoading(false)
        setVisible(true)
      })
  }, [])

  const handleSubmit = useCallback((event) => {
    if (resetForm.current) resetForm.current.handleSubmit(event)
  }, [])

  const changeMode = useCallback(() => setIsPhoneMode(!isPhoneMode),[isPhoneMode])

  const subtitle = useCallback(() => isPhoneMode
    ? translate("passwordResetScreen.forgotPasswordWithPhoneSubtitle")
    : translate("passwordResetScreen.forgotPasswordSubtitle"), [isPhoneMode])
    
  const buttonTitle = useCallback(() => isPhoneMode
    ? translate("passwordResetScreen.sendOTPButton")
    : translate("passwordResetScreen.ButtonForgotPassword"), [isPhoneMode])

  const modeButton = useCallback(() => isPhoneMode
    ? translate("passwordResetScreen.emailModeButton")
    : translate("passwordResetScreen.phoneModeButton")
  , [isPhoneMode])

  const handleBack = () => {
    setVisible(false);
    goBack('Login.Stack');
    setTimeout(() => {
      setMessage(null);
    }, 1000)
  }

  const ContentItem = useCallback(() => {
    return (
      <View>
        <Text preset="display2" my="24px" textAlign="center" tx="passwordResetScreen.guideTitle" colorScheme={colorScheme} />
        <Text preset="body2" textAlign="center" tx="passwordResetScreen.guideTitle" colorScheme={colorScheme} />
        <Text preset="title4" textAlign="center" text={resetForm.current && resetForm.current.values.email} colorScheme={colorScheme} />
        <Text preset="body2" my="16px" textAlign="center" tx="passwordResetScreen.guideMessage" colorScheme={colorScheme} />
      </View>
    )
  }, [colorScheme])

  const InputEmailOrPhoneForm = useCallback(() => {
    const params = {
      email: null,
      phone: null,
      isPhoneMode
    }
    return <PasswordResetForm colorScheme={colorScheme} onSubmit={onSubmit} innerRef={resetFormRef} initialValues={params} />
  }, [colorScheme, isPhoneMode, onSubmit, resetFormRef])

  return (
    <View flexGrow={1} justifyContent="center" px="16px">
      <AutoImage source={image} style={IMAGE} />
      <Text
        tx="passwordResetScreen.forgotPassword"
        preset="display2"
        colorScheme={colorScheme}
        mb="12px"
      />
      <Text
        preset="body2"
        mb="34px"
        text={subtitle()}
        colorScheme={colorScheme}
        color="gray.600"
      />
      <InputEmailOrPhoneForm />
      <View paddingY="9px">
        {!isPhoneMode && <Button
          testID="jala:id/next-screen-button"
          text={buttonTitle()}
          textPreset="title3"
          textColor={color.palette.white}
          colorScheme={colorScheme}
          onPress={handleSubmit}
          disabled={isError}
          loading={loading}
        />}
        {isPhoneMode && <Button
          testID="jala:id/next-screen-button"
          colorScheme={colorScheme}
          onPress={() => onRequestOTP('wa')}
          style={VERIF_BUTTON(true, color.palette.green500, isError)}
          disabled={isError}
        >
          <FontAwesomeIcon name="whatsapp" size={18} color={color.palette.white} />
          <Text tx="passwordResetScreen.sendWhatsappVerification" color={color.palette.white} numberOfLines={1} ml="8px" />
        </Button>}
        {isPhoneMode && <Button
          testID="jala:id/next-screen-button"
          colorScheme={colorScheme}
          onPress={() => onRequestOTP('sms')}
          style={VERIF_BUTTON(false, color.primary, isError)}
          disabled={isError}
        >
          <FontAwesomeIcon name="commenting-o" size={18} color={color.palette.white} />
          <Text tx="passwordResetScreen.sendSMSVerification" color={color.palette.white} numberOfLines={1} ml="8px" />
        </Button>}
        <Button
          testID="jala:id/next-screen-button"
          text={modeButton()}
          textPreset="title3"
          textColor={color.primary}
          colorScheme={colorScheme}
          onPress={changeMode}
          loading={loading}
          preset="link"
          style={LINK_BUTTON}
        />
      </View>
      <PopUp
        isVisible={isVisible}
        onPress={handleBack}
        error={message !== null}
        message={message}
        btnTx="common.back"
        colorScheme={colorScheme}
        textComponent={!message && <ContentItem />}
        title="passwordResetScreen.failedTitle"
      />
    </View>
  )
})

const InputOTP: FC<{
  colorScheme?: "light" | "dark",
  innerRef?: React.Ref<FormikProps<FormikValues>> | ((node) => void),
  onSetMode?: (mode?: string) => any,
  phone?: string,
  email?: string,
  via?: string,
  onSetToken?: (otp?: string) => void
}>  = observer(function InputOTP(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), onSetMode, phone, email, via, onSetToken } = props
  const [errors, setErrors] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [timer, setTimer] = useState<number>(0);
  const [expiredAt, setExpired] = useState<any>(null);
  const [isTimerOn, setIsTimerOn] = useState<boolean>(true);
  const [ isOTPSuccess, setIsOTPSuccess ] = useState<boolean>(true);

  const { isOpen, onClose, onOpen } = useDisclose()

  const image = require('./shrimp_otp.png')

  useEffect(() => {
    if (phone) getOTP();
  }, [phone])
  
  useEffect(() => {
    if (expiredAt) {
      let timeLeft = moment.utc(expiredAt).diff(moment.utc(), 'seconds');
      if (timeLeft <= 0) {
        timeLeft = 0;
        setIsTimerOn(false)
      }

      const intervalId = setInterval(() => {
        setTimer(timeLeft - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    }
    return () => timer;
  }, [expiredAt, setTimer, timer])

  const getOTP = useCallback(async () => {
    JalaAnalytic.log('Mobile app: reset password - request OTP', { phone: phone })
    await createAxios().post('password/phone/resend', { phone, via })
      .then((response) => {
        const { expired_at } = response.data;
        if(response.status === 200) {
          setExpired(expired_at || moment.utc().format('YYYY-MM-DD HH:mm:ss'));
          setIsOTPSuccess(true)
          setIsTimerOn(true);
        }
      })
      .catch((e) => {
        if (e.response && (e.response.data || {}).is_code_exist) {
          const { expired_at } = e.response.data;
          setExpired(expired_at || moment.utc().format('YYYY-MM-DD HH:mm:ss'));
        } else {
          setExpired(null);
        }
        setIsOTPSuccess(false)
      }).finally(() => onOpen())
  }, [phone, via]);

  const onResendOTP = useCallback(() => {
    if (!isTimerOn) getOTP();
  }, [getOTP, isTimerOn])

  const onContactPress = useCallback(() =>  {
    const message = translate('verificationScreen.whatsappMessageVerification', { phoneNumber: phone })
    const link = `https://api.whatsapp.com/send/?phone=6281325514194&text=${message}&app_absent=0`;
    Linking.openURL(link).catch();
    JalaAnalytic.log('Mobile app: reset password - contact us clicked');
  }, [phone])

  const handleChange = useCallback((value) => {
    setErrors(false);
    setOtp(value);
  }, [])

  const onConfirm = useCallback(() => {
    createAxios().post('password/phone/verify', { phone, code: otp })
      .then((response) => {
        const { token } = response.data;
        if(response.status === 200 && token) {
          onSetToken(token)
          onSetMode('password')
        }
      })
      .catch((error) => {
        setOtp("")
        if (error.response) {
          Alert.alert('Error', error.response.data.message, [{ text: 'OK', onPress: () => setErrors(false) }]);
        } else {
          Alert.alert('Error', error.message, [{ text: 'OK', onPress: () => setErrors(false) }]);
        }
      })
  }, [onSetMode, onSetToken, otp, phone])

  const valueTimer = !isOTPSuccess || timer <= 0 ? translate('passwordResetScreen.resendOTP') : `${timer}`;

  return (
      <View flexGrow={1} px="16px">
        <ResponseModal isOpen={isOpen} isSuccess={isOTPSuccess} onClose={() => onClose()} onContactPress={onContactPress} />
        <View flex={1} justifyContent="center">
          <AutoImage source={image} style={IMAGE} />
          <Text
            tx="passwordResetScreen.inputOTPTitle"
            preset="display2"
            colorScheme={colorScheme}
            mb="12px"
          />
          <Text
            preset="body2"
            text={translate("passwordResetScreen.inputOTPSubtitle", { phone })}
            colorScheme={colorScheme}
            color={color.palette.gray600}
          />
          <TouchableOpacity onPress={onResendOTP}>
            <Text text={valueTimer} preset="body2" colorScheme={colorScheme} color={color.primary} mb="34px" />
          </TouchableOpacity>
          <OTPInput
            colorScheme={colorScheme}
            handleChangeOTP={handleChange}
            errors={errors}
          />
        </View>
        <View paddingY="9px">
          <Button
            testID="jala:id/next-screen-button"
            tx="autoFeederScreen.send"
            textPreset="title3"
            textColor={color.palette.white}
            colorScheme={colorScheme}
            onPress={onConfirm}
            disabled={otp.length < 6}
          />
        </View>
      </View>
  )

})

const InputNewPassword: FC<{
  colorScheme?: "light" | "dark",
  innerRef?: React.Ref<FormikProps<FormikValues>> | ((node) => void),
  onSetMode?: (mode?: string) => any,
  token?: string,
  phone?: string
}>  = observer(function InputNewPassword(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), phone, onSetMode, token } = props

  const image = require('./shrimp_secret.png')

  const changePassword = useRef<FormikProps<FormikValues>>()
  const changePasswordRef = useCallback((node) => {
    changePassword.current = node;
    if (changePassword.current) {
      changePassword.current = node;
    }
  }, [])

  const onSubmit = useCallback(async ({ password, password_confirmation }) => {
    await createAxios().post('password/reset', { phone, token, password, password_confirmation })
      .then((response) => {
        if(response.status === 200) {
          onSetMode('success')
        }
      })
      .catch((error) => {
        if (error.response) {
          Alert.alert('Error', error.response.data.message, [{ text: 'OK' }]);
        } else {
          Alert.alert('Error', error.message, [{ text: 'OK' }]);
        }
      })
  }, [onSetMode, phone, token])

  const handleSubmitButton = useCallback((event) => {
    if (changePassword.current) changePassword.current.handleSubmit(event)
  }, [])

  const validationSchema = yup.object({
    password: yup.string().ascii().zxcvbn(1).min(6).required().label(translate("registerForm.password")),
    password_confirmation: yup.string().ascii()
      .oneOf([yup.ref('password')], translate("registerForm.passwordConfirmationDoesntMatch"))
      .required().label(translate("registerForm.passwordConfirmation")),
  })

  return (
      <View flexGrow={1} px="16px">
        <View height="120px" />
        <AutoImage source={image} style={IMAGE} />
        <Text
          tx="passwordResetScreen.resetPassword"
          preset="display2"
          colorScheme={colorScheme}
          mb="12px"
        />
        <Text
          preset="body2"
          mb="24px"
          tx="passwordResetScreen.inputNewPassword"
          colorScheme={colorScheme}
          color={color.palette.gray600}
        />
        <Formik
          initialValues={{ otp: null }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
          innerRef={changePasswordRef}
        >
          {({ isValid, handleSubmit }) => (
            <>
              <View flex={1}>
                <PasswordField name="password" isValid={isValid} handleSubmit={handleSubmit} colorScheme={colorScheme} />
                <PasswordField name="password_confirmation" isValid={isValid} handleSubmit={handleSubmit} colorScheme={colorScheme} isConfirmation />
              </View>
              <Button
                testID="jala:id/next-screen-button"
                tx="autoFeederScreen.send"
                textPreset="title3"
                textColor={color.palette.white}
                colorScheme={colorScheme}
                onPress={handleSubmitButton}
                disabled={!isValid}
                // loading={loading}
              />
            </>
          )}
        </Formik>
      </View>
  )
})

const SuccessResetPassword: FC<{ colorScheme?: "light" | "dark" }>  = observer(function InputOTP(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  const image = require('./shrimp_succed.png')

  const handleSubmit = useCallback(() => {
    goBack('Login.Stack');
  }, [])

  return (
      <View flexGrow={1} px="16px">
        <View flex={1} justifyContent="center">
          <AutoImage source={image} style={IMAGE} />
          <Text
            tx="passwordResetScreen.successTitle"
            preset="display2"
            colorScheme={colorScheme}
            textAlign="center"
            mb="12px"
          />
          <Text
            preset="body2"
            tx="passwordResetScreen.successSubtitle"
            colorScheme={colorScheme}
            textAlign="center"
            color={color.palette.gray600}
          />
        </View>
        <View paddingY="9px">
          <Button
            testID="jala:id/login-reset-success-button"
            tx="onboardScreen.login"
            textPreset="title3"
            textColor={color.palette.white}
            colorScheme={colorScheme}
            onPress={handleSubmit}
          />
        </View>
      </View>
  )

})

export const PasswordResetScreen: FC<{ colorScheme?: "light" | "dark" }>  = observer(function PasswordResetScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const [ mode, setMode ] = useState<string>("reset");
  const [ phoneNumber, setPhoneNumber ] = useState<string>('');
  const [ via, setVia ] = useState<string>('');
  const [ token, setToken ] = useState<string>('');
  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()
  const onSetMode = useCallback((mode) => setMode(mode), [])

  const onSetValue = useCallback((phone, via) => {
    setVia(via)
    setPhoneNumber(phone)
  }, [])

  const onSetToken = useCallback((token) => setToken(token), [])

  const ScreenDisplay = useCallback(() => {
    switch (mode) {
      case 'otp':
        return <InputOTP phone={phoneNumber} via={via} onSetMode={onSetMode} onSetToken={onSetToken} />
      case 'password':
        return <InputNewPassword token={token} phone={phoneNumber} onSetMode={onSetMode} />
      case 'success':
        return <SuccessResetPassword />
      default:
        return <InputEmailOrPhone onSetValue={onSetValue} onSetMode={onSetMode} />;
    }
  }, [mode, onSetMode, onSetToken, onSetValue, phoneNumber, token, via])

  return(
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <ScreenDisplay />
    </Screen>
  )
  
})
